/* Apply form thank-you page — fetches the public calendar URL (configured in
 * the branding_settings collection or APPLY_CALENDAR_URL env var) and offers
 * the candidate to book their introductory call.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Calendar, Check, Home } from "lucide-react";
import axios from "axios";
import { useLang } from "@/context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApplyThanksPage() {
  const { t } = useLang();
  const location = useLocation();
  const firstName = location.state?.firstName || "";
  const [config, setConfig] = useState({ calendar_url: "", support_email: "" });

  useEffect(() => {
    document.title = t("comp_applythankspage_90");
    axios.get(`${API}/public/apply-config`).then((r) => setConfig(r.data || {})).catch(() => {});
  }, [t]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-wine-50/30 via-white to-amber-50/20 px-4 py-12 sm:px-6 lg:px-8" data-testid="apply-thanks">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-3xl border border-wine-100 bg-white p-8 text-center shadow-sm sm:p-12">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-500 text-white shadow-lg">
            <Check className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-serif text-4xl font-bold text-navy">{t("apply_thanks_title")}{firstName ? `, ${firstName}` : ""} 🍷</h1>
          <p className="mt-4 text-base text-neutral-700">
            {t("apply_thanks_intro")}<br className="hidden sm:block" />
            {t("apply_thanks_within")} <strong className="text-wine-700">{t("apply_thanks_within_value")}</strong>.
          </p>

          {config.calendar_url ? (
            <div className="mt-8 rounded-2xl border border-wine-200 bg-wine-50/40 p-6 text-left">
              <div className="flex items-start gap-3">
                <Calendar className="mt-0.5 h-6 w-6 shrink-0 text-wine-600" />
                <div className="flex-1">
                  <h2 className="font-serif text-xl font-bold text-navy">{t("apply_thanks_calendar_h")}</h2>
                  <p className="mt-1 text-sm text-neutral-700" dangerouslySetInnerHTML={{ __html: t("apply_thanks_calendar_desc") }} />
                  <a href={config.calendar_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full bg-wine-500 px-6 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-wine-600" data-testid="apply-calendar-cta">
                    <Calendar className="h-4 w-4" /> {t("apply_thanks_book_cta")}
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-8 rounded-2xl bg-neutral-50 px-5 py-4 text-sm text-neutral-700">
              {t("apply_thanks_no_calendar")}
            </div>
          )}

          <div className="mt-8 grid gap-3 text-left text-sm text-neutral-700 sm:grid-cols-2">
            <div className="rounded-2xl border border-neutral-100 bg-neutral-50/60 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-wine-700">{t("apply_thanks_follow")}</div>
              <div className="mt-2 flex flex-wrap gap-3 text-sm">
                <a href="https://www.linkedin.com/company/la-winetech/" target="_blank" rel="noreferrer" className="hover:text-wine-700">LinkedIn</a>
                <a href="https://www.instagram.com/la_winetech/" target="_blank" rel="noreferrer" className="hover:text-wine-700">Instagram</a>
                <a href="https://www.youtube.com/@LaWineTech" target="_blank" rel="noreferrer" className="hover:text-wine-700">YouTube</a>
              </div>
            </div>
            <div className="rounded-2xl border border-neutral-100 bg-neutral-50/60 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-wine-700">{t("apply_thanks_questions")}</div>
              <a href={`mailto:${config.support_email || "contact@lawinetech.com"}`} className="mt-2 inline-block text-sm font-semibold text-wine-700 hover:underline">{config.support_email || "contact@lawinetech.com"}</a>
            </div>
          </div>

          <Link to="/" className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-5 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50" data-testid="apply-back-home">
            <Home className="h-4 w-4" /> {t("apply_thanks_back_home")}
          </Link>
        </div>
      </div>
    </div>
  );
}
